import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, Spin, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';
import OnboardingWizard from '../components/OnboardingWizard';
import GroupOnboardingForm from '../components/GroupOnboardingForm';
import useCustomerCreation from '../hooks/useCustomerCreation';
import { OnboardingStep } from '../types';
import {useGetCategoryById} from "@/shared/api/client/customer-category-controller";
import {useGetFieldConfigurationForCategoryGrouped} from "@/shared/api/client/field-configuration-controller";

const { Title, Paragraph } = Typography;

const GroupOnboardingView: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  
  const {
    selectedCategory,
    setSelectedCategory,
    currentStep,
    steps,
    setSteps,
    setCurrentStep,
    resetOnboarding,
    getCompletionPercentage,
    formData,
  } = useOnboardingStore();

  const { createGroupCustomer, isSubmitting } = useCustomerCreation();

  const {
    data: category,
    isLoading: categoryLoading,
    error: categoryError,
  } = useGetCategoryById(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });

  // Fetch field configuration for GROUP type
  const {
    data: fieldConfiguration,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldConfigurationForCategoryGrouped(
    Number(categoryId),
    {
      query: {
        enabled: !!categoryId,
      },
    }
  );

  // Get member template ID from parentCategoryId
  // Note: Groups reference their member template via parentCategoryId
  const memberTemplateId = useMemo(() => {
    return category?.parentCategoryId || null;
  }, [category]);

  // Get wizard orientation from UI config
  const wizardOrientation = useMemo(() => {
    return category?.uiConfiguration?.wizardOrientation || 'horizontal';
  }, [category]);

  const showProgress = useMemo(() => {
    return category?.uiConfiguration?.showProgress !== false;
  }, [category]);


  // Set up steps whenever category or fieldConfiguration changes
  useEffect(() => {
    if (!category || !fieldConfiguration) {
      return;
    }

    console.log('GroupOnboardingView: Setting up steps', {
      categoryId: category.id,
      categoryName: category.name,
      parentCategoryId: category.parentCategoryId,
      memberTemplateId,
      fieldGroups: fieldConfiguration.fieldGroups?.length,
      currentStepsLength: steps.length,
      selectedCategoryId: selectedCategory?.id
    });
    
    // Only update selectedCategory if it's different to avoid unnecessary re-renders
    if (!selectedCategory || selectedCategory.id !== category.id) {
      setSelectedCategory(category);
    }
    
    // Create steps from field groups (for GROUP information)
    const fieldGroupSteps: OnboardingStep[] = fieldConfiguration.fieldGroups
      ?.filter(group => group.isVisible)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map((group, index) => ({
        id: group.groupKey ?? `step-${index}`,
        title: group.groupName ?? '',
        description: group.description || `Complete ${group.groupName} information`,
        component: 'FieldGroup',
        isRequired: group.fields?.some(field => field.defaultRequired) || false,
        isCompleted: false,
        fieldGroup: group, // Store the field group data for rendering
      })) || [];

    // Add additional steps for members, products, locations, documents, review
    const additionalSteps: OnboardingStep[] = [
      {
        id: 'group-members',
        title: 'Group Members',
        description: 'Add and manage group members',
        component: 'GroupMemberManagement',
        isRequired: true,
        isCompleted: false,
      },
      {
        id: 'locations',
        title: 'Address Information',
        description: 'Group address and location details',
        component: 'LocationForm',
        isRequired: true,
        isCompleted: false,
      },
      {
        id: 'documents',
        title: 'Document Upload',
        description: 'Upload required group documents',
        component: 'DocumentUploadForm',
        isRequired: true,
        isCompleted: false,
      },
      {
        id: 'review',
        title: 'Review & Submit',
        description: 'Review all information before submission',
        component: 'ReviewForm',
        isRequired: true,
        isCompleted: false,
      },
    ];
    
    const allSteps = [...fieldGroupSteps, ...additionalSteps];
    console.log('GroupOnboardingView: Created steps', {
      totalSteps: allSteps.length,
      fieldGroupSteps: fieldGroupSteps.length,
      additionalSteps: additionalSteps.length,
      stepIds: allSteps.map(s => s.id)
    });
    
    setSteps(allSteps);
  }, [category, fieldConfiguration, memberTemplateId, selectedCategory, setSelectedCategory, setSteps]);

  // Separate effect to reset currentStep if it's out of bounds
  useEffect(() => {
    if (steps.length > 0 && currentStep >= steps.length) {
      console.log('GroupOnboardingView: Resetting currentStep from', currentStep, 'to 0');
      setCurrentStep(0);
    }
  }, [steps, currentStep, setCurrentStep]);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      console.log('='.repeat(80));
      console.log('GROUP ONBOARDING SUBMISSION - COMPLETE PAYLOAD');
      console.log('='.repeat(80));
      console.log('Category ID:', categoryId);
      console.log('Category Name:', category?.name);
      console.log('Member Template ID:', memberTemplateId);
      console.log('\n--- FORM DATA ---');
      console.log(JSON.stringify(formData, null, 2));
      console.log('\n--- STEPS ---');
      console.log(JSON.stringify(steps, null, 2));
      console.log('='.repeat(80));

      // Add memberTemplateId to formData for API submission
      const enrichedFormData = {
        ...formData,
        customerData: {
          ...formData.customerData,
          memberTemplateId: memberTemplateId,
        },
      };

      const result = await createGroupCustomer(
        Number(categoryId),
        enrichedFormData
      );

      if (result.success) {
        message.success(result.message);
        // Navigate back to category selection after successful submission
        navigate('/customers/onboarding');
        resetOnboarding();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to create group. Please try again.');
      console.error('Submission error:', error);
    }
  };

  const handleCancel = () => {
    navigate('/customer-onboarding');
    resetOnboarding();
  };

  if (categoryLoading || fieldsLoading) {
    return (
      <div className="onboarding-loading">
        <Spin size="large" />
        <Paragraph>Loading onboarding configuration...</Paragraph>
      </div>
    );
  }

  if (categoryError || fieldsError || !category) {
    return (
      <div className="onboarding-error">
        <Alert
          message="Configuration Error"
          description="Unable to load onboarding configuration for this category."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/customer-onboarding')}>
              Back to Categories
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="group-onboarding-view">
      <div className="onboarding-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer-onboarding')}
          className="back-button"
        >
          Back to Categories
        </Button>
        
        <div className="category-info">
          <Title level={3}>{category.name}</Title>
          <Paragraph>{category.description}</Paragraph>
        </div>
      </div>

      <OnboardingWizard
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        canProceed={true}
        isSubmitting={isSubmitting}
        orientation={wizardOrientation as 'horizontal' | 'vertical'}
        showProgress={showProgress}
      >
        <GroupOnboardingForm
          categoryId={Number(categoryId)}
          currentStep={currentStep}
          fieldConfiguration={fieldConfiguration}
          memberTemplateId={memberTemplateId}
          categoryProducts={category?.products || []}
          categoryLocations={category?.locations || []}
          categoryDocuments={category?.documents || []}
        />
      </OnboardingWizard>
    </div>
  );
};

export default GroupOnboardingView;
